import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, TrendingUp, Coins } from "lucide-react";
import { api, formatCrypto } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function Staking() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [stakeForm, setStakeForm] = useState({ asset: "ETH", amount: "" });
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    const [p, pos, w] = await Promise.all([
      api.get("/staking/products"),
      api.get("/staking/positions"),
      api.get("/wallet"),
    ]);
    setProducts(p.data.products);
    setPositions(pos.data.positions);
    setWallet(w.data);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(() => setTick((tt) => tt + 1), 1000);
    const j = setInterval(load, 30_000);
    return () => { clearInterval(i); clearInterval(j); };
  }, [load]);

  // Live counter — extrapolate accrual since last fetch
  const livePositions = useMemo(() => {
    return positions.map((p) => {
      const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
      const reward = (p.principal * p.apr * elapsed) / (365 * 24 * 3600);
      return { ...p, live_reward: reward, live_value: p.principal + reward };
    });
  }, [positions, tick]);

  const stake = async () => {
    const n = Number(stakeForm.amount);
    if (!n || n <= 0) return toast.error(t("staking.enter_amount"));
    setBusy(true);
    try {
      await api.post("/staking/stake", { asset: stakeForm.asset, amount: n });
      toast.success(`${t("staking.staked_success")} ${n} ${stakeForm.asset}`);
      setStakeForm({ ...stakeForm, amount: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("staking.stake_failed"));
    } finally {
      setBusy(false);
    }
  };

  const unstake = async (id) => {
    if (!confirm(t("staking.unstake_confirm"))) return;
    try {
      const r = await api.post("/staking/unstake", { position_id: id });
      toast.success(`+${r.data.credited.toFixed(8)} ${t("staking.unstake_credited")}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("staking.unstake_failed"));
    }
  };

  const available = (asset) =>
    wallet?.balances.find((b) => b.currency === asset)?.balance || 0;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("staking.eyebrow")}</div>
        <h1 className="font-display text-3xl font-bold mt-1">{t("staking.title")}</h1>
        <p className="text-sm text-[#A3A9B8] mt-2 max-w-xl">
          {t("staking.subtitle")}
        </p>
      </div>

      {/* Products */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2A2E38]">
        {products.map((p) => (
          <div key={p.asset} className="bg-[#141720] p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{p.asset}</div>
                <h3 className="font-display text-2xl font-bold mt-1">{p.name}</h3>
              </div>
              <Coins size={20} className="text-[#7C5CFF]" />
            </div>
            <div className="mt-4 flex items-end gap-3">
              <div className="font-mono text-4xl text-[#7C9C69]">
                {(p.apr * 100).toFixed(2)}%
              </div>
              <div className="text-xs text-[#6E7587] mb-1.5">{t("staking.apr")}</div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-[#A3A9B8]">
              <div><span className="text-[#6E7587]">{t("staking.min_stake")}</span> {p.min_stake} {p.asset}</div>
              <div><span className="text-[#6E7587]">{t("staking.lock")}</span> {p.lock_days === 0 ? t("staking.flexible") : `${p.lock_days}${t("staking.days")}`}</div>
            </div>
            <button
              data-testid={`staking-select-${p.asset}`}
              onClick={() => setStakeForm({ asset: p.asset, amount: "" })}
              className={`mt-5 w-full px-4 py-2 text-sm rounded-sm border transition-colors ${
                stakeForm.asset === p.asset
                  ? "bg-[#7C5CFF] border-[#7C5CFF] text-white"
                  : "border-[#2A2E38] text-[#F4F1EB] hover:border-[#7C5CFF] hover:text-[#7C5CFF]"
              }`}
            >
              {stakeForm.asset === p.asset ? t("staking.selected") : `${t("staking.stake")} ${p.asset}`}
            </button>
          </div>
        ))}
      </div>

      {/* Stake form */}
      <div className="border border-[#2A2E38] bg-[#141720] p-6 md:p-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{t("staking.new_position")}</div>
        <h3 className="font-display text-xl font-bold mt-1">
          {t("staking.stake")} {stakeForm.asset}
        </h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">
                {t("staking.amount")}
              </label>
              <span className="text-xs text-[#6E7587] font-mono">
                {t("staking.available")} {formatCrypto(available(stakeForm.asset))} {stakeForm.asset}
              </span>
            </div>
            <input
              data-testid="staking-amount"
              type="number"
              step="any"
              min="0"
              value={stakeForm.amount}
              onChange={(e) => setStakeForm({ ...stakeForm, amount: e.target.value })}
              placeholder="0.00"
              className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 font-mono focus:border-[#7C5CFF] focus:outline-none"
            />
          </div>
          <div className="flex items-end">
            <button
              data-testid="staking-submit"
              disabled={busy || !stakeForm.amount}
              onClick={stake}
              className="w-full px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Lock size={14} /> {busy ? t("staking.staking") : `${t("staking.stake")} ${stakeForm.asset}`}
            </button>
          </div>
        </div>
      </div>

      {/* Active positions */}
      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="px-6 py-4 border-b border-[#2A2E38]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{t("staking.active_positions")}</div>
          <h3 className="font-display text-lg font-bold mt-1">{t("staking.your_stakes")}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
              <tr className="border-b border-[#2A2E38]">
                <th className="text-left px-6 py-3 font-normal">{t("staking.col_asset")}</th>
                <th className="text-left px-6 py-3 font-normal hidden sm:table-cell">{t("staking.col_started")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("staking.col_principal")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("staking.col_apr")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("staking.col_reward")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("staking.col_value")}</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {livePositions.map((p) => (
                <tr key={p.id} data-testid={`staking-position-${p.id}`} className="border-b border-[#2A2E38] hover:bg-[#1C202B]">
                  <td className="px-6 py-4 font-medium">{p.asset}</td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <div className="text-xs text-[#A3A9B8] font-mono">
                      {new Date(p.started_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </div>
                    <div className="text-[10px] text-[#6E7587] font-mono">
                      {new Date(p.started_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-mono">{formatCrypto(p.principal)}</td>
                  <td className="px-6 py-4 text-right font-mono text-[#7C9C69]">{(p.apr * 100).toFixed(2)}%</td>
                  <td className="px-6 py-4 text-right font-mono text-[#7C9C69]">
                    +{p.live_reward.toFixed(10)}
                  </td>
                  <td className="px-6 py-4 text-right font-mono">{formatCrypto(p.live_value, 10)}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      data-testid={`staking-unstake-${p.id}`}
                      onClick={() => unstake(p.id)}
                      className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] inline-flex items-center gap-1"
                    >
                      <Unlock size={12} /> {t("staking.unstake")}
                    </button>
                  </td>
                </tr>
              ))}
              {livePositions.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#6E7587]">
                  {t("staking.no_stakes")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
